/**
 * useCapabilities — Phase B tier-aware feature gating (Zustand).
 *
 * Single source of truth for "what can THIS user do?". Backed by
 * `/api/account/me/capabilities`. Pages and SideNav read from this
 * to decide whether to render gated features.
 *
 * Pairs with the backend's `require_capability` route guard so a
 * customer can't bypass a hidden UI — the server enforces the same
 * rule.
 */
import { create } from 'zustand'

import { getCapabilities } from '@/lib/api'
import type { Capabilities } from '@/lib/types'

interface CapabilitiesState {
  capabilities: Capabilities | null
  loading: boolean
  error: string | null

  fetch: () => Promise<void>
  reset: () => void
}

export const useCapabilities = create<CapabilitiesState>()((set, get) => ({
  capabilities: null,
  loading: false,
  error: null,

  fetch: async () => {
    if (get().loading) return
    set({ loading: true, error: null })
    try {
      const capabilities = await getCapabilities()
      set({ capabilities, loading: false })
    } catch (e) {
      set({ error: String(e), loading: false })
    }
  },

  reset: () => set({ capabilities: null, loading: false, error: null }),
}))

// Convenience selectors so callers don't reach into the dict.
export const useCanSeeScenarios = (): boolean =>
  useCapabilities((s) => s.capabilities?.can_see_scenarios ?? false)

export const useCanSeeRecommendations = (): boolean =>
  useCapabilities((s) => s.capabilities?.can_see_recommendations ?? false)

export const useCanAutoExecute = (): boolean =>
  useCapabilities((s) => s.capabilities?.can_auto_execute ?? false)

export const useIsOwnerTier = (): boolean =>
  useCapabilities((s) => s.capabilities?.is_owner ?? false)
